import { useLocaleStore } from "@/store/locale";
import { TRANSLATIONS, type TranslationLanguage } from "@/lib/i18n/translations";

/**
 * Hook to get translations based on current language
 * @param ssrLanguage - Optional language code for SSR (bypasses store)
 * @returns Object with translation function and current language
 */
export function useTranslation(ssrLanguage?: string) {
  const storeLanguage = useLocaleStore((state) => state.currentLanguage) as TranslationLanguage;

  // Use SSR language if provided (for server-side rendering), otherwise use store
  const currentLanguage = (ssrLanguage || storeLanguage) as TranslationLanguage;

  /**
   * Get translated text for a key
   * @param key - Translation key in format "category.key" (e.g., "common.addToCart")
   * @param replacements - Optional object with values to replace placeholders
   * @returns Translated string
   */
  const t = (key: string, replacements?: Record<string, string | number>): string => {
    const keys = key.split(".");
    let value: any = TRANSLATIONS[currentLanguage] || TRANSLATIONS.en;

    // Navigate through nested keys
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        console.warn(`Translation key not found: ${key} for language: ${currentLanguage}`);
        return key; // Return key if translation not found
      }
    }

    // If no replacements, return value
    if (!replacements || typeof value !== "string") {
      return value;
    }

    // Replace placeholders like {count} with actual values
    let result = value;
    for (const [placeholder, replacement] of Object.entries(replacements)) {
      result = result.replace(new RegExp(`\\{${placeholder}\\}`, "g"), String(replacement));
    }

    return result;
  };

  return { t, currentLanguage };
}
